import React, { Suspense, useState, useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import NoSsr from '@mui/material/NoSsr';
import './app.css';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { Router, Route, Redirect } from 'react-router-dom';
import { whitelabel } from '#config/whitelabel';
import { fetchAccount } from '#action/user/settings';
import ServiceWorkerController from '#service-worker/controller';
import UserLocalStorage from '#utils/user-local-storage';
import Header from './header';
import { Dashboard } from './dashboard';
import { DevicesDashboard, DevicesDashboardMap } from './device/dashboard';
import DeviceOverview from './device/overview';
import DeviceLog from './device/log';
import ProjectDashboard from './project/dashboard';
import ProjectLog from './project/project-log';
import AddProject from './project/add';
import EditProject from './project/edit';
import Reports from './reports';
import ExcursionReportForm from './reports/excursion-report';
import UserDashboard from './user/dashboard';
import UserSettings from './user/settings';
import AddUser from './user/add';
import VerifyUser from './user/verify';
import UserLogin from './user/login';
import UserLog from './user/log';
import ForgotPassword from './user/forgot-password';
import NotificationComponent from './notification';
import ShipmentDashboard from './shipment/dashboard';
import ShipmentOverview from './shipment/overview';
import ShipmentForm from './shipment/form';
import ShipmentLog from './shipment/log';
import SiteManager from './warehouse/site-manager';
import ApplianceCardsView from './warehouse/cards-view';
import ApplianceOverview from './warehouse/appliance-overview';
import WarehouseForm from './warehouse/warehouse-form';
import WarehouseLog from './warehouse/warehouse-log';
import AddApplianceForm from './warehouse/appliance-form/add';
import EditApplianceForm from './warehouse/appliance-form/edit';
import theme from './theme';
import PreloaderComponent from './preloader';
import Backdrop from './backdrop';
import propTypes from './prop-types';
import ErrorBoundary from './error-boundary';
import AddressbookDashboard from './addressbook/dashboard';
import AddressbookForm from './addressbook/addressbook-form';
import ContactForm from './addressbook/contact-form';
import RedirectUserWithoutProjects from './general/redirect-user-without-projects';
import ProtectedRoute from './routes/protected-route';
import AuthChallenge from './user/auth-challenge';
import { useServiceWorker, usePatchTimezone, useTracking } from '#hooks';
import { loadScript, loadLink, getDeviceRouteWithoutProjectId, parseQueryState } from '#helper';
import { history } from '#configureStore';

const DeviceLabel = React.lazy(() => import('./export/device-label'));
const ShipmentOverviewDeeplink = React.lazy(() => import('./deeplink/overview'));

const { VITE_MAPS_JS_API: API_KEY } = import.meta.env;

const unprotectedRoutes = [
  { path: '/login', exact: true },
  { path: '/auth-challenge', exact: true },
  { path: '/forgotpassword' },
  { path: '/verifypassword' },
  { path: '/link' }
];

const routesWithoutHeader = [
  ...unprotectedRoutes,
  { path: '/export' }
];

const isRouteMatches = ({ path, exact }) => {
  const { pathname } = history.location;
  if (exact) {
    return pathname === path;
  }
  return pathname.includes(path);
};

const Head = props => {
  const { location, classes, account, socket } = props;
  if (routesWithoutHeader.some(r => isRouteMatches(r))) return null;
  return (
    <Header
      classes={classes}
      location={location}
      account={account}
      socket={socket}
    />
  );
};

const App = ({ socket }) => {
  const [, setGoogle] = useState(false);
  const [mapbox, setMapbox] = useState(false);
  const dispatch = useDispatch();
  const account = useSelector(s => s.account, shallowEqual);

  useEffect(() => {
    const { pathname } = history.location;
    const { location: { search } } = window;
    const { beforeLoginPath } = parseQueryState(search);

    const isExcludedPath = path => {
      const excludedPaths = ['/', '/login', '/auth-challenge', '/forgotpassword', '/build/user_visible_auto_notification'];

      return excludedPaths.includes(path) || /verifypassword|link/.test(path);
    };

    if (!isExcludedPath(pathname)) {
      localStorage.setItem('beforeLoginPath', pathname);
    }

    if (pathname === '/login' && beforeLoginPath) {
      localStorage.setItem('beforeLoginPath', beforeLoginPath);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (unprotectedRoutes.every(ur => !isRouteMatches(ur))) {
        await dispatch(fetchAccount());
      }
    };
    fetchData();
    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&v=quarterly`,
      () => setGoogle(true)
    );
    loadLink(
      'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css'
    );
    loadScript(
      'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.js',
      () => setMapbox(true)
    );
  }, [dispatch]);

  useLayoutEffect(() => {
    if (account.id) {
      UserLocalStorage.setUser(account.id);
    }

    document.getElementById('root').addEventListener('transitionend', () => {
      window.dispatchEvent(new Event('resize'));
    }, false);
  }, [account.id]);

  usePatchTimezone();

  useServiceWorker();

  useTracking();

  useEffect(() => {
    const removeSW = async () => {
      await ServiceWorkerController.unregisterSW();
      window.location.href = '/login';
    };

    if (account.logout && unprotectedRoutes.every(ur => !isRouteMatches(ur))) {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        removeSW();
      } else {
        window.location.href = '/login';
      }
    }
  }, [account.logout]);

  const loading = (account.requestStatus !== 'succeeded' || account.requestStatus === 'idle') && unprotectedRoutes.every(ur => !isRouteMatches(ur));
  const mainPage = account.defaultPage || '/dashboard';

  return (
    <NoSsr>
      <Router history={history}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme(whitelabel.getConfig())}>
            <ErrorBoundary>
              {loading ? (
                <PreloaderComponent />
              ) : (
                <RedirectUserWithoutProjects
                  unprotectedRoutes={unprotectedRoutes}
                  isRouteMatches={isRouteMatches}
                >
                  <CssBaseline />
                  <NotificationComponent />
                  <Backdrop socket={socket} />
                  <Route
                    path="/"
                    render={() => (
                      <Head socket={socket} account={account} />
                    )}
                  />
                  <Route
                    exact
                    path={[
                      '/',
                      '/build/user_visible_auto_notification'
                    ]}
                    render={() => (
                      <Redirect to={mainPage} />
                    )}
                  />
                  <Route
                    exact
                    path={[
                      '/dashboard',
                      '/dashboard/:tabNum'
                    ]}
                    component={Dashboard}
                  />
                  <Route exact path="/devices" render={() => <Redirect to="/device" />} />
                  <Route exact path="/device" component={DevicesDashboard} />
                  <Route exact path="/devices/map" render={() => <Redirect to="/device-map" />} />
                  <Route exact path="/device-map" render={p => (mapbox ? <DevicesDashboardMap {...p} /> : null)} />
                  <Route
                    exact
                    path={[
                      '/device/:deviceId',
                      '/device/:deviceId/t/:tabNum/',
                      '/device/:deviceId/t/:tabNum/dr/:startDate/:endDate'
                    ]}
                    component={DeviceOverview}
                  />
                  <Route
                    exact
                    path="/device-log"
                    component={DeviceLog}
                  />
                  <ProtectedRoute exact path="/project" component={ProjectDashboard} />
                  <ProtectedRoute exact path="/project/edit/:id" component={EditProject} />
                  <Route exact path="/project/log" render={() => <Redirect to="/project-log" />} />
                  <ProtectedRoute exact path="/project-log" component={ProjectLog} />
                  <ProtectedRoute exact path="/addproject" component={AddProject} />
                  <ProtectedRoute exact path="/user" component={UserDashboard} />
                  <ProtectedRoute exact path="/adduser" component={AddUser} />
                  <Route
                    exact
                    path="/verifypassword/:token"
                    component={VerifyUser}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/user/:id',
                      '/profile/:id'
                    ]}
                    render={props => (
                      <UserSettings key={props.match.params.id} {...props} />
                    )}
                  />
                  <ProtectedRoute
                    exact
                    path="/user-log"
                    component={UserLog}
                  />
                  <Route
                    exact
                    path="/login"
                    render={props => <UserLogin socket={socket} {...props} />}
                  />
                  <Route
                    exact
                    path="/auth-challenge"
                    render={props => <AuthChallenge socket={socket} {...props} />}
                  />
                  <Route
                    exact
                    path="/forgotpassword/:sent?"
                    component={ForgotPassword}
                  />
                  <ProtectedRoute
                    exact
                    path="/shipment"
                    component={ShipmentDashboard}
                  />
                  <ProtectedRoute
                    exact
                    path="/shipment-log"
                    component={ShipmentLog}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/shipment/:shipmentId',
                      '/shipment/:shipmentId/t/:tabNum'
                    ]}
                    component={ShipmentOverview}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/addshipment',
                      '/shipment/edit/:id/'
                    ]}
                    component={ShipmentForm}
                  />
                  <ProtectedRoute
                    exact
                    path="/reports"
                    component={Reports}
                  />
                  <ProtectedRoute
                    exact
                    path="/reports/application-excursion"
                    component={ExcursionReportForm}
                  />
                  <ProtectedRoute
                    exact
                    path="/warehouse-manager"
                    component={SiteManager}
                  />
                  <ProtectedRoute
                    exact
                    path="/warehouse"
                    component={ApplianceCardsView}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/warehouse/appliance-overview/:applianceId',
                      '/warehouse/appliance-overview/:applianceId/t/:tabNum',
                      '/warehouse/appliance-overview/:applianceId/t/:tabNum/dr/:startDate/:endDate'
                    ]}
                    component={ApplianceOverview}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/create-warehouse',
                      '/warehouse/edit/:id'
                    ]}
                    component={WarehouseForm}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/warehouse/appliance/create',
                      '/warehouse/appliance/create/:warehouseId'
                    ]}
                    component={AddApplianceForm}
                  />
                  <ProtectedRoute
                    exact
                    path="/warehouse/appliance/edit/:applianceId"
                    component={EditApplianceForm}
                  />
                  <ProtectedRoute
                    exact
                    path="/warehouse-log"
                    component={WarehouseLog}
                  />
                  <ProtectedRoute exact path="/addressbook/page/:pageNum" render={() => <Redirect to="/addressbook" />} />
                  <ProtectedRoute exact path="/addressbook" component={AddressbookDashboard} />
                  <ProtectedRoute exact path="/create-addressbook" render={() => <Redirect to="/addressbook/create" />} />
                  <ProtectedRoute
                    exact
                    path={[
                      '/addressbook/create',
                      '/addressbook/edit/:id'
                    ]}
                    component={AddressbookForm}
                  />
                  <ProtectedRoute
                    exact
                    path={[
                      '/addressbook/contact/create',
                      '/addressbook/contact/edit/:id'
                    ]}
                    component={ContactForm}
                  />
                  <Route
                    exact
                    path={[
                      '/device/:projectId(\\d+)/:deviceId(\\d+)/settings',
                      '/device/:projectId(\\d+)/:deviceId(\\d+)',
                      '/device/:projectId(\\d+)/:deviceId(\\d+)/t/:tabNum/',
                      '/device/:projectId(\\d+)/:deviceId(\\d+)/t/:tabNum/dr/:startDate/:endDate'
                    ]}
                    render={({ match }) => (
                      <Redirect to={getDeviceRouteWithoutProjectId(match.url)} />
                    )}
                  />
                  <Suspense fallback={<PreloaderComponent />}>
                    <Route
                      exact
                      path="/export/device/:deviceId/label"
                      component={DeviceLabel}
                    />
                    <Route
                      exact
                      path={[
                        '/link/shipment/:token',
                        '/link/shipment/:token/t/:tabNum'
                      ]}
                      component={ShipmentOverviewDeeplink}
                    />
                  </Suspense>
                </RedirectUserWithoutProjects>
              )}
            </ErrorBoundary>
          </ThemeProvider>
        </StyledEngineProvider>
      </Router>
    </NoSsr>
  );
};

App.propTypes = propTypes;

export default App;
